// Desktop-only header surface hardening.
// Applies an opaque, layered header on desktop and restores React's original inline
// styles when the viewport moves back into the mobile layout.
const originalStyles = new WeakMap();
const touched = new Set();

const remember = element => {
  if (!element) return;
  if (!originalStyles.has(element)) originalStyles.set(element, element.getAttribute('style'));
  touched.add(element);
};

const restoreMobileStyles = () => {
  touched.forEach(element => {
    if (!element?.isConnected) return;
    const original = originalStyles.get(element);
    if (original === null || original === undefined) element.removeAttribute('style');
    else element.setAttribute('style', original);
    if (element.dataset?.vcDesktopHeaderFixed) delete element.dataset.vcDesktopHeaderFixed;
  });
  touched.clear();
};

const applyDesktopHeaderSurfaceFix = () => {
  if (window.innerWidth <= 768) {
    restoreMobileStyles();
    return;
  }

  const bellIcon = document.querySelector('.lucide-bell, .lucide-bell-off');
  const bellButton = bellIcon?.closest('button');
  if (!bellButton) return;

  let header = bellButton.parentElement;
  while (header && header !== document.body) {
    const style = window.getComputedStyle(header);
    if (style.position === 'sticky' && style.top === '0px') break;
    header = header.parentElement;
  }
  if (!header || header === document.body) return;

  remember(header);
  header.dataset.vcDesktopHeaderFixed = 'true';
  Object.assign(header.style, {
    background: 'linear-gradient(180deg, rgba(5,12,24,0.995) 0%, rgba(7,16,31,0.99) 100%)',
    backdropFilter: 'none',
    WebkitBackdropFilter: 'none',
    borderBottom: '1px solid rgba(96,165,250,.18)',
    boxShadow: '0 10px 34px rgba(0,0,0,.42), 0 1px 0 rgba(255,255,255,.035)',
    zIndex: '1500',
    isolation: 'isolate',
    overflow: 'visible'
  });

  const actionGroup = bellButton.parentElement?.parentElement;
  if (actionGroup) {
    remember(actionGroup);
    actionGroup.style.position = 'relative';
    actionGroup.style.zIndex = '1502';
  }

  remember(bellButton);
  Object.assign(bellButton.style, {
    background: 'rgba(10,22,42,.98)',
    backdropFilter: 'none',
    WebkitBackdropFilter: 'none',
    border: '1px solid rgba(96,165,250,.22)',
    boxShadow: '0 6px 18px rgba(0,0,0,.28), inset 0 1px 0 rgba(255,255,255,.055)',
    position: 'relative',
    zIndex: '1503'
  });

  if (actionGroup) {
    const buttons = [...actionGroup.querySelectorAll(':scope > div > button')];
    buttons.forEach(button => {
      remember(button);
      Object.assign(button.style, {
        background: 'rgba(10,22,42,.98)',
        backdropFilter: 'none',
        WebkitBackdropFilter: 'none',
        border: '1px solid rgba(96,165,250,.18)',
        boxShadow: '0 6px 18px rgba(0,0,0,.24), inset 0 1px 0 rgba(255,255,255,.05)',
        position: 'relative',
        zIndex: '1503'
      });
      const wrapper = button.parentElement;
      if (wrapper) {
        remember(wrapper);
        wrapper.style.position = 'relative';
        wrapper.style.zIndex = '1503';
      }
    });

    [...actionGroup.querySelectorAll(':scope > div > div')].forEach(panel => {
      if (panel !== bellButton.parentElement && panel !== actionGroup) {
        remember(panel);
        panel.style.zIndex = '1600';
      }
    });
  }
};

let raf = null;
const scheduleFix = () => {
  if (raf) cancelAnimationFrame(raf);
  raf = requestAnimationFrame(applyDesktopHeaderSurfaceFix);
};

const observer = new MutationObserver(scheduleFix);
observer.observe(document.documentElement, { childList: true, subtree: true });
window.addEventListener('resize', scheduleFix);
window.addEventListener('orientationchange', scheduleFix);
window.addEventListener('load', scheduleFix);
document.addEventListener('DOMContentLoaded', scheduleFix, { once: true });
setTimeout(scheduleFix, 300);
setTimeout(scheduleFix, 1200);
